import React, { useEffect, useRef, useState } from 'react';
import { Loader2, type LucideIcon } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'glossy';
  size?: 'sm' | 'md' | 'lg';
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  fullWidth?: boolean;
  className?: string;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  icon: Icon,
  iconPosition = 'left',
  loading = false,
  fullWidth = false,
  className = '',
  disabled,
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variantClasses = {
    primary: 'bg-accent1 text-black hover:bg-yellow-400 active:bg-yellow-600 shadow-sm hover:shadow-md',
    secondary: 'bg-secondary-600 text-white hover:bg-secondary-700 active:bg-secondary-800 shadow-sm hover:shadow-md',
    outline: 'border-2 border-accent1 text-accent1 hover:bg-accent1/10 dark:hover:bg-accent1/20 active:bg-accent1/20 dark:active:bg-accent1/30',
    ghost: 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 active:bg-gray-200 dark:active:bg-gray-700',
    danger: 'bg-red-500 text-white hover:bg-red-600 active:bg-red-700 shadow-sm hover:shadow-md',
    glossy: 'bg-gray-900 text-white rounded-full border border-white/10 shadow-sm hover:shadow-md',
  } as const;

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };

  const iconSize = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-5 w-5',
  };

  const combinedClasses = `
    ${baseClasses}
    ${variantClasses[variant]}
    ${sizeClasses[size]}
    ${fullWidth ? 'w-full' : ''}
    ${variant === 'glossy' ? 'relative overflow-hidden' : ''}
    ${className}
  `.trim();

  const iconClasses = `${iconSize[size]} ${
    iconPosition === 'right' ? 'ms-2' : 'me-2'
  }`;

  // Measure element to draw a stroke that matches the real border radius
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [metrics, setMetrics] = useState<{ width: number; height: number; radius: number }>({ width: 0, height: 0, radius: 0 });
  useEffect(() => {
    if (!buttonRef.current) return;
    const el = buttonRef.current;
    const update = () => {
      const rect = el.getBoundingClientRect();
      const styles = window.getComputedStyle(el);
      const tl = parseFloat(styles.borderTopLeftRadius || '0');
      const tr = parseFloat(styles.borderTopRightRadius || '0');
      const br = parseFloat(styles.borderBottomRightRadius || '0');
      const bl = parseFloat(styles.borderBottomLeftRadius || '0');
      const radius = Math.max(tl, tr, br, bl);
      setMetrics({ width: Math.max(1, Math.round(rect.width)), height: Math.max(1, Math.round(rect.height)), radius });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <button
      className={combinedClasses}
      disabled={disabled || loading}
      ref={buttonRef}
      {...props}
    >
      {variant === 'glossy' && (
        <>
          {/* Animated racing border along the outline (SVG stroke) */}
          {metrics.width > 0 && metrics.height > 0 && (
            <span className="pointer-events-none absolute inset-0 rounded-[inherit] z-20">
              <svg width={metrics.width} height={metrics.height} viewBox={`0 0 ${metrics.width} ${metrics.height}`} preserveAspectRatio="none">
                <rect
                  x={1}
                  y={1}
                  width={metrics.width - 2}
                  height={metrics.height - 2}
                  rx={metrics.radius}
                  ry={metrics.radius}
                  fill="none"
                  stroke="#facc15"
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                  pathLength={100}
                  strokeDasharray="15 85"
                >
                  <animate attributeName="stroke-dashoffset" from="0" to="-100" dur="2.5s" repeatCount="indefinite" />
                </rect>
              </svg>
            </span>
          )}
          {/* Glow (yellow) */}
          <span
            className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-70 z-0"
            style={{
              background:
                'radial-gradient(60% 120% at 80% 60%, rgba(250,204,21,0.25), rgba(250,204,21,0) 60%)',
            }}
          />
          {/* Gloss (top) */}
          <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-[inherit] bg-gradient-to-b from-white/30 to-transparent z-20" />
        </>
      )}
      <span className={variant === 'glossy' ? 'relative z-30 inline-flex items-center' : ''}>
      {loading ? (
        <Loader2 className="me-2 h-4 w-4 animate-spin" />
      ) : (
        <>
          {Icon && iconPosition === 'left' && (
            <Icon className={iconClasses} />
          )}
          {children}
          {Icon && iconPosition === 'right' && (
            <Icon className={iconClasses} />
          )}
        </>
      )}
      </span>
    </button>
  );
};